import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { isEmptyField, isEmptyObject, isNullOrUndefined } from '../common-utils';
import { environment } from '../environments/environment.dev';
import { VlDataService } from '../vl-data/vl-data.service';
import { APIMapping } from './api-mapping.cnst';
import { defaultHTTPConfig } from './default-http-config.cnst';
import { HTTPAPIConfig } from './http-api.config';
import { HttpUtilsService } from './http-utils.service';

@Injectable({
  providedIn: 'root'
})
export class VlHttpService {
  /**
   * API_Mapping object referred to the APIMappingConstant
   */
  private API_MAPPING: any = APIMapping;
  /**
   * URL_Mapping object referred to the environment urlMapping
   */
  private URL_MAPPING: any = environment.urlMapping;
  /**
   * defaultHTTPConfig object referred to the defaultHTTPConfig constant
   */
  private defaultHTTPConfig: HTTPAPIConfig = defaultHTTPConfig;

  constructor(
    /**
     * http client module from the angular/common/http
     */
    private http: HttpClient,
    /**
     * vl data service
     */
    private dataService: VlDataService,
    /**
     * httpUtilsService
     */
    private httpUtilService: HttpUtilsService
  ) {}
  /**
   * will return the options merged with default options
   * @param config HTTPAPIConfig
   * return Options Interface
   */

  private getOptions(config?: HTTPAPIConfig): any {
    if (config && isEmptyField(config?.options)) {
      config.options = {};
    }
  
    const options = this.dataService.deepMerge(this.defaultHTTPConfig.options, config?.options);
    const optionHeaders = this.dataService.deepClone(options.headers);
    const optionParams = this.dataService.deepClone(options.params);
  
    this.populateOptions(optionHeaders, 'headers', options);
    this.populateOptions(optionParams, 'params', options);
  
    return options;
  }
  
  private populateOptions(optionObj: any, optionKey: string, options: any) {
    if (!isEmptyField(optionObj) && Object.keys(optionObj).length > 0) {
      const result: any = {};
      for (const key in optionObj) {
        if (optionObj.hasOwnProperty(key)) {
          result[key] = optionObj[key];
        }
      }
      options[optionKey] = result;
    }
  }
  
  /**
   * main method need to call from the http service
   * @param config HTTPAPIConfig
   */
  public placeAPICall(config: HTTPAPIConfig): Observable<any> {
    return this.processConfig(config);
  }
  public placeMultipleAPICalls(
    apiConfigArray: HTTPAPIConfig[]
  ): Observable<any> {
    const joinList: any = [];
    apiConfigArray.forEach((apiConfig) => {
      joinList.push(this.processConfig(apiConfig));
    });
    return forkJoin(joinList);
  }

  private processConfig(config: HTTPAPIConfig): Observable<any> {
    // tslint:disable-next-line: prefer-const

    const actualConfig: HTTPAPIConfig = this.dataService.deepMerge(this.defaultHTTPConfig, config);
    if (isEmptyField(actualConfig.urlString)) {
      actualConfig.urlString = 'PROJ_API';
    }
    if (isEmptyField(actualConfig.isEntireUrl)) {
      actualConfig.isEntireUrl = false;
    }
    // please uncomment after the you have added the authService
    // if (!isEmptyField(config.userFieldsToAdd)) {
    //   actualConfig.postData = this.httpUtilService.addUserFields(config.userFieldsToAdd, actualConfig.postData);
    // }
    if (!isEmptyObject(config.routerParamsToAdd)) {
      actualConfig.postData = this.httpUtilService.addRouteFields(
        config,
        actualConfig.postData
      );
    }
      if (!isEmptyField(config.postDataFormatter)) {
        const { functionName, formatter } = config.postDataFormatter || {};
      
        if (!isEmptyField(functionName)) {
            const func = this.httpUtilService[functionName as keyof typeof this.httpUtilService];
            if (typeof func === 'function') {
              actualConfig.postData = func(actualConfig.postData, config);
            }
        }
      
        if (formatter && typeof formatter === 'function') {
          actualConfig.postData = formatter(actualConfig.postData, config);
        }
      }

    const urlStringMap: string = this.URL_MAPPING[actualConfig.urlString!];
    const apiStatus: any = this.isValidAPICall(
      actualConfig.apiKeyName,
      urlStringMap,
      actualConfig.isEntireUrl!
    );
    let finalUrl: any = apiStatus.api;

    if (!actualConfig.isEntireUrl) {
      finalUrl = urlStringMap + apiStatus.api;
    }
    if (apiStatus.valid) {
      return this[actualConfig.method](
        finalUrl,
        actualConfig.postData,
        actualConfig
      );
    } else {
      return throwError(() => ({ status: false, msg: apiStatus.msg }));
    }
  }
  /**
   * instantiate post method
   * @param url finalUrl to get called
   * @param postData postData
   * @param config apiConfig
   * return Observable with http call
   */
  private post(
    url: string,
    postData: any,
    config: HTTPAPIConfig
  ): Observable<any> { 
    let splitUrl = url.split('/');
    if (Array.isArray(postData)) {
      return this.http.post(url, postData, this.getOptions(config));
    }
    for (const postVal in postData) {
      if (postData.hasOwnProperty(postVal)) {
        const paramIndex = splitUrl.indexOf(':' + postVal);
        if (paramIndex !== -1) {
          splitUrl[paramIndex] = postData[postVal];
        }
      }
    }
    const keysToRemove = this.getColonPrefixedKeys(url);
    postData = this.removeKeys(postData, keysToRemove);
    url = splitUrl.join('/'); 
    return this.http.post(url, postData, this.getOptions(config));
  }

  private patch(
    url: string,
    postData: any,
    config: HTTPAPIConfig
  ): Observable<any> {
    const splitUrl = url.split('/');
    for (const postVal in postData) {
      if (postData.hasOwnProperty(postVal)) {
        const paramIndex = splitUrl.indexOf(':' + postVal);
        if (paramIndex !== -1) {
          splitUrl[paramIndex] = postData[postVal];
        }
      }
    }
    const keysToRemove = this.getColonPrefixedKeys(url);
    postData = this.removeKeys(postData, keysToRemove);
    url = splitUrl.join('/');
    return this.http.patch(url, postData, this.getOptions(config));
  }

  private put(
    url: string,
    postData: any,
    config: HTTPAPIConfig
  ): Observable<any> {
    const splitUrl = url.split('/');
    for (const postVal in postData) {
      if (postData.hasOwnProperty(postVal)) {
        const paramIndex = splitUrl.indexOf(':' + postVal);
        if (paramIndex !== -1) {
          splitUrl[paramIndex] = postData[postVal];
        }
      }
    }
    url = splitUrl.join('/'); 
    return this.http.put(url, postData, this.getOptions(config));
  }


  private delete(
    url: string,
    postData: any,
    config: HTTPAPIConfig
  ): Observable<any> {
    const splitUrl = url.split('/');
    for (const postVal in postData) {
      if (postData.hasOwnProperty(postVal)) {
        const paramIndex = splitUrl.indexOf(':' + postVal);
        if (paramIndex !== -1) {
          splitUrl[paramIndex] = postData[postVal];
        }
      }
    }
    url = splitUrl.join('/');
    return this.http.delete(url, {
      body: postData,
      ...this.getOptions(config)
    });
  }

  /**
   * instantiate multipart method
   * @param url finalUrl to get called
   * @param postData postData
   * @param config apiConfig
   * return Observable with http call
   */
  private multipart(
    url: string,
    postData: any,
    config: HTTPAPIConfig
  ): Observable<any> {
    const fd = this.createFormData(postData);
    const splitUrl = url.split('/');
    for (const postVal in postData) {
      if (postData.hasOwnProperty(postVal)) {
        const paramIndex = splitUrl.indexOf(':' + postVal);
        if (paramIndex !== -1) {
          splitUrl[paramIndex] = postData[postVal];
        }
      }
    }
    url = splitUrl.join('/');
    return this.http.post(url, fd, this.getOptions(config));
  }
  /**
   * will convert object to flat structure
   * @param object object to get converted as form data
   * @param form form to attach
   * @param namespace namespace to be added
   * return formData with flat Structure
   */
  // tslint:disable-next-line: ban-types
  private createFormData(
    object: any,
    form?: FormData,
    namespace?: string
  ): FormData {
    const formData = form || new FormData();
    for (const property in object) {
      if (
        !object.hasOwnProperty(property) ||
        isNullOrUndefined(object[property])
      ) {
        continue;
      }
      const formKey = namespace ? `${namespace}[${property}]` : property;
      if (object[property] instanceof Date) {
        formData.append(formKey, object[property].toISOString());
      } else if (
        typeof object[property] === 'object' &&
        !(object[property] instanceof File)
      ) {
        this.createFormData(object[property], formData, formKey);
      } else {
        formData.append(formKey, object[property]);
      }
    }
    return formData;
  }
  /**
   * instantiate get method
   * @param url finalUrl to get called
   * @param postData postData
   * @param config apiConfig
   * return Observable with http call
   */
  private get(
    url: string,
    postData: any,
    config: HTTPAPIConfig
  ): Observable<any> {
    const splitUrl = url.split('/');
    for (const postVal in postData) {
      if (postData.hasOwnProperty(postVal)) {
        const paramIndex = splitUrl.indexOf(':' + postVal);
        if (paramIndex !== -1) {
          splitUrl[paramIndex] = postData[postVal];
        }
      }
    }
    url = splitUrl.join('/');
    return this.http.get(url, this.getOptions(config));
  }
  /**
   * verify the status of api whether it is present or not in the api mapping
   * @param api api from the apiConfig
   * @param url url from the urlMapping
   * @param isEntireUrl boolean for isEntireUrl or not
   * return success or failure
   */
  private isValidAPICall(
    api: string,
    url: string,
    isEntireUrl: boolean
  ): object {
    if (isEntireUrl) {
      return { valid: true, api: this.API_MAPPING[api] };
    }
    if (!isNullOrUndefined(this.API_MAPPING)) {
      const currentApi = this.API_MAPPING[api];
      if (!isNullOrUndefined(currentApi)) {
        return { valid: true, api: currentApi };
      } else {
        return { valid: false, msg: 'API_MAPPING not found for ' + api };
      }
    } else {
      return {
        valid: false,
        msg: 'API_MAPPING not found in Project Constants (ProjConst)',
      };
    }
  }

  getColonPrefixedKeys(url: any) {
    return url
      .split('/')                       // Split URL by '/'
      .filter((segment: any) => segment.startsWith(':')) // Filter segments starting with `:`
      .map((segment: any) => segment.slice(1)); // Remove the `:` prefix
  }

  removeKeys(params: any, keysToRemove: any) {
    const result = { ...params };  // Create a shallow copy of params

    keysToRemove.forEach((key: any) => {
      delete result[key];           // Remove each key from the copied object
    });
    return result;
  }

}
